/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Log } from '../../core/logging';
import type { TivoPayloadAction } from '../../core/tivo/tivo-redux-wrapper';
import {
    biaxialWidgetUpdateCaretVisibleAction,
    biaxialWidgetUpdateHorizontalItemsAction,
    biaxialWidgetUpdateTurboScrollAction,
    biaxialWidgetUpdateVerticalItemsAction,
} from './biaxialwidget.actions';
import {
    BWHListType,
    THorizontalItem,
    TScrollDirection,
    TVerticalItems,
} from './biaxialwidget.props';
import {
    BIAXIAL_WIDGET_CLEAR_DATA,
    BIAXIAL_WIDGET_UPDATE_CARET_VISIBLE,
    BIAXIAL_WIDGET_UPDATE_HORIZONTAL_ITEMS,
    BIAXIAL_WIDGET_UPDATE_HORIZONTAL_POSITION,
    BIAXIAL_WIDGET_UPDATE_TURBO_SCROLL,
    BIAXIAL_WIDGET_UPDATE_VERTICAL_ITEMS,
    BIAXIAL_WIDGET_UPDATE_VERTICAL_POSITION,
} from './biaxialwidget.types';

const TAG = 'biaxialWidget';
const initialState: {
    turboScrollDirection: TScrollDirection | null;
    horizontalItem: THorizontalItem;
    horizontalPosition: number;
    isCaretVisible: boolean;
    isTurboScroll: boolean;
    verticalItems: TVerticalItems;
    verticalPosition: number;
} = {
    turboScrollDirection: null,
    horizontalItem: {
        listType: BWHListType.DEFAULT,
        listItems: [],
        isEmpty: false,
    },
    horizontalPosition: 0,
    isCaretVisible: false,
    isTurboScroll: false,
    verticalItems: [],
    verticalPosition: 0,
};

const biaxialWidgetReducers = (
    state = initialState,
    { type, payload } = <TivoPayloadAction<typeof initialState>>{},
) => {
    switch (type) {
        case BIAXIAL_WIDGET_CLEAR_DATA:
            return {
                ...state,
                turboScrollDirection: payload.turboScrollDirection,
                horizontalItem: payload.horizontalItem,
                horizontalPosition: payload.horizontalPosition,
                isCaretVisible: payload.isCaretVisible,
                isTurboScroll: payload.isTurboScroll,
                verticalItems: payload.verticalItems,
                verticalPosition: payload.verticalPosition,
            };
        case BIAXIAL_WIDGET_UPDATE_CARET_VISIBLE:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case BIAXIAL_WIDGET_UPDATE_HORIZONTAL_ITEMS:
            return {
                ...state,
                horizontalItem: payload.horizontalItem,
            };
        case BIAXIAL_WIDGET_UPDATE_VERTICAL_ITEMS:
            return {
                ...state,
                verticalItems: payload.verticalItems,
            };
        case BIAXIAL_WIDGET_UPDATE_HORIZONTAL_POSITION:
            return {
                ...state,
                horizontalPosition: payload.horizontalPosition,
            };
        case BIAXIAL_WIDGET_UPDATE_TURBO_SCROLL:
            return {
                ...state,
                isTurboScroll: payload.isTurboScroll,
                turboScrollDirection: payload.turboScrollDirection,
            };
        case BIAXIAL_WIDGET_UPDATE_VERTICAL_POSITION:
            return {
                ...state,
                verticalPosition: payload.verticalPosition,
            };
        default:
            return state;
    }
};

export const biaxialWidgetUpdateCaretVisible =
    (isCaretVisible: boolean) => (dispatch: Function) => {
        dispatch(biaxialWidgetUpdateCaretVisibleAction(isCaretVisible));
    };

export const biaxialWidgetDisableTurboScroll = () => (dispatch: Function) => {
    dispatch(biaxialWidgetUpdateTurboScrollAction(false, null));

    Log.i(TAG, 'biaxialWidgetDisableTurboScroll action dispatched');
};

export const biaxialWidgetEnableTurboScroll =
    (turboScrollDirection: TScrollDirection) => (dispatch: Function) => {
        dispatch(biaxialWidgetUpdateTurboScrollAction(true, turboScrollDirection));

        Log.i(TAG, 'biaxialWidgetEnableTurboScroll action dispatched');
    };

export const biaxialWidgetUpdateHorizontalItems =
    (horizontalItem: THorizontalItem) => (dispatch: Function) => {
        dispatch(biaxialWidgetUpdateHorizontalItemsAction(horizontalItem));

        Log.i(TAG, 'biaxialWidgetUpdateHorizontalItems action dispatched');
    };

export const biaxialWidgetUpdateVerticalItems =
    (verticalItems: TVerticalItems) => (dispatch: Function) => {
        dispatch(biaxialWidgetUpdateVerticalItemsAction(verticalItems));

        Log.i(TAG, 'biaxialWidgetUpdateVerticalItems action dispatched');
    };

export default biaxialWidgetReducers;
